import { computed } from '@angular/core';
import { patchState, signalStoreFeature, withComputed, withMethods, withState } from '@ngrx/signals';
import { ConstructionProjectType } from '@construction/construction.enums';
import { ConstructionProjectLoadModel } from '@construction/models/construction-project.model';
import { EquipmentSpecsType } from '@construction/modules/project-edit/modules/construction-equipment-specs/construction-equipment-specs.types';
import { LockConstructionProjectStatesEnum } from '@construction/modules/project-edit/modules/construction-schedule/construction-schedule.enums';
import { ContactProfileModel } from '@user/user.component';

/** Owns `project`, `lastEventStatus`, `lastDroneImage`, and derived computed signals (projectId, projectType, capacityByProjectType, projectUsers). */
export function withProjectData() {
  return signalStoreFeature(
    withState({
      project: {} as ConstructionProjectLoadModel,
      lastEventStatus: undefined as string | undefined,
      lastDroneImage: null as string | Date | null,
    }),
    withComputed((store) => ({
      projectId: computed(() => store.project()._id),
      projectType: computed(() => store.project().project_types),
      isDt3Enabled: computed(() => store.project().internal_feature_flags?.dt3 ?? false),
      capacityByProjectType: computed(() => {
        const projectTypes = store.project().project_types;
        const isSolar = projectTypes?.some(({ name }) => name === ConstructionProjectType.Solar);
        const isBess = projectTypes?.some(({ name }) => name === ConstructionProjectType.Bess);

        if (isBess && isSolar) return 'MWp / MWh';
        if (isBess) return 'MWh';

        return ' MWp';
      }),
      projectUsers: computed((): ContactProfileModel[] => {
        const project = store.project();

        return [
          ...(project.owner_construction_managers || []),
          ...(project.epc_cr_quality_managers || []),
          ...(project.epc_quality_managers || []),
        ].sort((a, b) =>
          `${a.first_name} ${a.last_name}`.toLowerCase().localeCompare(`${b.first_name} ${b.last_name}`.toLowerCase()),
        );
      }),
    })),
    withMethods((store) => ({
      updateEquipmentSpecs(equipmentSpecs: EquipmentSpecsType): void {
        patchState(store, { project: { ...store.project(), equipment_specs: equipmentSpecs } });
      },
      updateConstructionProjectData(constructionProject: ConstructionProjectLoadModel): void {
        patchState(store, { project: { ...store.project(), ...constructionProject } });
      },
      updateLastEventStatus(status: LockConstructionProjectStatesEnum): void {
        patchState(store, { lastEventStatus: status });
      },
      setLastDroneImage(lastDroneImage: string | Date | null): void {
        patchState(store, { lastDroneImage });
      },
    })),
  );
}
